import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Scale, Ruler, Footprints, Pill, Droplets, Syringe, TrendingDown, Equal } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';

export function BodyInfoDialog({
  newBodyweight,
  setNewBodyweight,
  weightUnit,
  setWeightUnit,
  waistMeasurement,
  setWaistMeasurement,
  waistUnit,
  setWaistUnit,
  bodyHeight,
  setBodyHeight,
  heightUnit,
  setHeightUnit,
  userAge,
  setUserAge,
  userGender,
  setUserGender,
  strideLength,
  setStrideLength,
  medicationsEnabled,
  setMedicationsEnabled,
  weightGoal,
  setWeightGoal,
  weightGoalStartDate,
  setWeightGoalStartDate,
  diabetesEnabled,
  setDiabetesEnabled,
  diabetesUnit,
  setDiabetesUnit,
  diabetesInsulin,
  setDiabetesInsulin,
  diabetesType,
  setDiabetesType,
  onSave,
}) {
  const handleStrideLengthChange = (e) => {
    const inputVal = e.target.value;
    if (inputVal === '') {
      setStrideLength('');
      return;
    }
    const val = parseInt(inputVal);
    if (!isNaN(val)) {
      setStrideLength(val);
      if (val >= 40 && val <= 120) {
        localStorage.setItem('gym_notebook_walking_stride_length', val.toString());
      }
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button className="flex flex-col items-center gap-2 p-4 bg-slate-800/50 rounded-2xl hover:bg-slate-800 transition-colors border border-slate-700/50">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
            <Scale className="h-5 w-5 text-white" />
          </div>
          <span className="text-sm text-white">Body Info</span>
        </button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
              <Scale className="h-4 w-4 text-white" />
            </div>
            Body Information
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          {/* Current Bodyweight */}
          <div className="space-y-2">
            <Label className="text-white flex items-center gap-2">
              <Scale className="h-4 w-4 text-green-400" />
              Current Bodyweight
            </Label>
            <div className="flex gap-2">
              <Input
                type="number"
                value={newBodyweight}
                onChange={(e) => setNewBodyweight(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white flex-1"
                placeholder="Enter your weight"
              />
              <div className="flex gap-1 bg-slate-700 rounded-lg p-1">
                <button
                  onClick={() => setWeightUnit('kg')}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    weightUnit === 'kg' ? 'bg-green-500 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  kg
                </button>
                <button
                  onClick={() => setWeightUnit('lbs')}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    weightUnit === 'lbs' ? 'bg-green-500 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  lbs
                </button>
              </div>
            </div>
          </div>
          
          {/* Waist Circumference */}
          <div className="space-y-2">
            <Label className="text-white flex items-center gap-2">
              <Ruler className="h-4 w-4 text-orange-400" />
              Waist Circumference
            </Label>
            <div className="flex gap-2">
              <Input
                type="number"
                value={waistMeasurement}
                onChange={(e) => setWaistMeasurement(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white flex-1"
                placeholder="Enter waist size"
              />
              <div className="flex gap-1 bg-slate-700 rounded-lg p-1">
                <button
                  onClick={() => setWaistUnit('cm')}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    waistUnit === 'cm' ? 'bg-orange-500 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  cm
                </button>
                <button
                  onClick={() => setWaistUnit('in')}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    waistUnit === 'in' ? 'bg-orange-500 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  in
                </button>
              </div>
            </div>
            <p className="text-[10px] text-slate-500">
              Track your waist over time in Statistics
            </p>
          </div>
          
          {/* Height */}
          <div className="space-y-2">
            <Label className="text-white flex items-center gap-2">
              <Ruler className="h-4 w-4 text-blue-400" />
              Height
            </Label>
            <div className="flex gap-2">
              <Input
                type="number"
                value={bodyHeight}
                onChange={(e) => setBodyHeight(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white flex-1"
                placeholder="Enter your height"
              />
              <div className="flex gap-1 bg-slate-700 rounded-lg p-1">
                <button
                  onClick={() => setHeightUnit('cm')}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    heightUnit === 'cm' ? 'bg-blue-500 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  cm
                </button>
                <button
                  onClick={() => setHeightUnit('ft')}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    heightUnit === 'ft' ? 'bg-blue-500 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  ft
                </button>
              </div>
            </div>
          </div>
          
          {/* Age */}
          <div className="space-y-2">
            <Label className="text-white">Age</Label>
            <Input
              type="number"
              value={userAge}
              onChange={(e) => setUserAge(e.target.value)}
              className="bg-slate-700 border-slate-600 text-white"
              placeholder="Enter your age"
            />
          </div>
          
          {/* Gender */}
          <div className="space-y-2">
            <Label className="text-white">Gender</Label>
            <div className="flex gap-2">
              <button
                onClick={() => setUserGender('male')}
                className={`flex-1 py-2 rounded-lg text-sm transition-colors ${
                  userGender === 'male' ? 'bg-blue-500 text-white' : 'bg-slate-700 text-slate-400 hover:text-white'
                }`}
              >
                Male
              </button>
              <button
                onClick={() => setUserGender('female')}
                className={`flex-1 py-2 rounded-lg text-sm transition-colors ${
                  userGender === 'female' ? 'bg-pink-500 text-white' : 'bg-slate-700 text-slate-400 hover:text-white'
                }`}
              >
                Female
              </button>
            </div>
          </div>
          
          {/* Weight Goal */}
          <div className="space-y-2 p-3 bg-slate-700/30 rounded-lg">
            <Label className="text-white text-sm font-medium">Weight Goal</Label>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setWeightGoal('maintain');
                window.dispatchEvent(new Event('bodyDataChanged'));
                }}
                className={`flex-1 py-2 rounded-lg text-sm transition-colors flex items-center justify-center gap-1.5 ${
                  weightGoal === 'maintain' ? 'bg-green-500 text-white' : 'bg-slate-700 text-slate-400 hover:text-white'
                }`}
                data-testid="goal-maintain"
              >
                <Equal className="h-3.5 w-3.5" />
                Maintain
              </button>
              <button
                onClick={() => {
                  setWeightGoal('lose');
                window.dispatchEvent(new Event('bodyDataChanged'));
                  if (!weightGoalStartDate) {
                    const today = new Date().toISOString().split('T')[0];
                    setWeightGoalStartDate(today);
                    localStorage.setItem('gym_notebook_weight_goal_start', today);
                  }
                }}
                className={`flex-1 py-2 rounded-lg text-sm transition-colors flex items-center justify-center gap-1.5 ${
                  weightGoal === 'lose' ? 'bg-red-500 text-white' : 'bg-slate-700 text-slate-400 hover:text-white'
                }`}
                data-testid="goal-lose"
              >
                <TrendingDown className="h-3.5 w-3.5" />
                Lose Weight
              </button>
            </div>
            {weightGoal === 'lose' && (
              <div className="mt-2">
                <Label className="text-slate-400 text-xs">Start Date</Label>
                <Input
                  type="date"
                  value={weightGoalStartDate}
                  onChange={(e) => {
                    setWeightGoalStartDate(e.target.value);
                    localStorage.setItem('gym_notebook_weight_goal_start', e.target.value);
                  }}
                  className="bg-slate-700 border-slate-600 text-white mt-1 text-sm"
                  data-testid="goal-start-date"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  12-week progressive deficit, then 2 months maintain, then repeat.
                </p>
              </div>
            )}
          </div>
          
          {/* Medication Reminders Toggle */}
          <div className="space-y-2 p-3 bg-slate-700/30 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Pill className="h-4 w-4 text-teal-400" />
                <Label className="text-white text-sm font-medium">Medication Reminders</Label>
              </div>
              <Switch
                checked={medicationsEnabled}
                onCheckedChange={setMedicationsEnabled}
                data-testid="medications-toggle"
              />
            </div>
            <p className="text-[10px] text-slate-500">
              Show Medications on the main dashboard for tracking meds & reminders.
            </p>
          </div>

          {/* Diabetes Toggle */}
          <div className="space-y-2 p-3 bg-slate-700/30 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Droplets className="h-4 w-4 text-rose-400" />
                <Label className="text-white text-sm font-medium">Diabetes</Label>
              </div>
              <Switch
                checked={diabetesEnabled}
                onCheckedChange={setDiabetesEnabled}
                data-testid="diabetes-toggle"
              />
            </div>
            <p className="text-[10px] text-slate-500">
              Enable Diabetes Diary for blood sugar tracking and mealtime glucose logging.
            </p>
          </div>

          {/* Diabetes sub-settings (only shown when diabetes is ON) */}
          {diabetesEnabled && (
            <>
              {/* Units */}
              <div className="space-y-2 p-3 bg-rose-500/5 border border-rose-500/20 rounded-lg ml-3">
                <div className="flex items-center justify-between">
                  <Label className="text-white text-sm font-medium">Blood Sugar Units</Label>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setDiabetesUnit('mmol/l')}
                      className={`px-3 py-1 text-xs rounded-lg transition-all ${
                        diabetesUnit === 'mmol/l'
                          ? 'bg-rose-500/30 text-rose-300 border border-rose-500/50'
                          : 'bg-slate-700/50 text-slate-400 border border-slate-600 hover:border-slate-500'
                      }`}
                      data-testid="unit-mmol"
                    >
                      mmol/L
                    </button>
                    <button
                      onClick={() => setDiabetesUnit('mg/dl')}
                      className={`px-3 py-1 text-xs rounded-lg transition-all ${
                        diabetesUnit === 'mg/dl'
                          ? 'bg-rose-500/30 text-rose-300 border border-rose-500/50'
                          : 'bg-slate-700/50 text-slate-400 border border-slate-600 hover:border-slate-500'
                      }`}
                      data-testid="unit-mgdl"
                    >
                      mg/dL
                    </button>
                  </div>
                </div>
              </div>

              {/* Insulin */}
              <div className="space-y-2 p-3 bg-rose-500/5 border border-rose-500/20 rounded-lg ml-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Syringe className="h-4 w-4 text-sky-400" />
                    <Label className="text-white text-sm font-medium">Insulin</Label>
                  </div>
                  <Switch
                    checked={diabetesInsulin}
                    onCheckedChange={setDiabetesInsulin}
                    data-testid="insulin-toggle"
                  />
                </div>
                <p className="text-[10px] text-slate-500">
                  Track insulin doses (short-acting & long-acting) with blood sugar entries.
                </p>
              </div>

              {/* Diabetes Type (only when insulin is ON) */}
              {diabetesInsulin && (
                <div className="space-y-2 p-3 bg-sky-500/5 border border-sky-500/20 rounded-lg ml-3">
                  <Label className="text-white text-sm font-medium">Diabetes Type</Label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setDiabetesType('type2')}
                      className={`flex-1 px-3 py-2 text-xs rounded-lg transition-all ${
                        diabetesType === 'type2'
                          ? 'bg-sky-500/30 text-sky-300 border border-sky-500/50'
                          : 'bg-slate-700/50 text-slate-400 border border-slate-600 hover:border-slate-500'
                      }`}
                      data-testid="diabetes-type2"
                    >
                      Type 2
                    </button>
                    <button
                      onClick={() => setDiabetesType('type1')}
                      className={`flex-1 px-3 py-2 text-xs rounded-lg transition-all ${
                        diabetesType === 'type1'
                          ? 'bg-sky-500/30 text-sky-300 border border-sky-500/50'
                          : 'bg-slate-700/50 text-slate-400 border border-slate-600 hover:border-slate-500'
                      }`}
                      data-testid="diabetes-type1"
                    >
                      Type 1
                    </button>
                  </div>
                  <p className="text-[10px] text-slate-500">
                    Used for ISF calculation: Type 1 (75/TDI) or Type 2 (90/TDI).
                  </p>
                </div>
              )}
            </>
          )}

          {/* Stride Length */}
          <div className="space-y-2 p-3 bg-slate-700/30 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Footprints className="h-4 w-4 text-blue-400" />
                <Label className="text-white text-sm font-medium">Stride Length</Label>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={strideLength}
                  onChange={handleStrideLengthChange}
                  className="w-20 h-8 text-center bg-slate-700 border-slate-600 text-white text-sm"
                  min="40"
                  max="120"
                  placeholder="75"
                />
                <span className="text-sm text-slate-400">cm</span>
              </div>
            </div>
            <p className="text-[10px] text-slate-500">
              For GPS step counting. Average: 65-75cm (women), 75-85cm (men)
            </p>
          </div>
          
          <p className="text-xs text-slate-500">
            Height, age & gender are used for BMR calculation in Nutrition Tracker.
          </p>
          
          <Button
            onClick={() => {
              onSave();
              window.dispatchEvent(new Event('bodyDataChanged'));
            }}
            className="w-full bg-blue-500 hover:bg-blue-400"
            data-testid="update-bodyweight-btn"
          >
            Save Body Info
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default BodyInfoDialog;
